import type { Appointment } from '~/models/appointments'
import type { AppointmentWithContainerInfo } from '~/models/appointmentWithContainerInfo'
import type { LinkedAppointment } from '~/services/apiClient'
import { TransactionDirection } from '~/services/apiClient'
import { AppointmentTransaction } from '~/models/groupedAppointments'

function computeSortKey(tx: AppointmentTransaction): number {
  const appt = tx.inbound_appointment ?? tx.outbound_appointment
  if (!appt) {
    return Number.MAX_SAFE_INTEGER
  }
  const ms = appt.appointment.window_start.toMillis()
  const id = appt.appointment.id
  return ms * 1e4 + id
}

export function groupAppointmentsIntoTransactions(
  appointments: AppointmentWithContainerInfo[]
): AppointmentTransaction[] {
  const relevantAppointments =
    limitToReleventTransactionsPerContainerAndMoveType(appointments)
  const groups = groupAppointmentsIntoLinkedGroups(relevantAppointments)
  const transactions = groups.map(createTransactionFromGroup)

  const keyed: { tx: AppointmentTransaction; key: number }[] = transactions.map(
    (tx) => ({ tx, key: computeSortKey(tx) })
  )

  keyed.sort((a, b) => a.key - b.key)

  return keyed.map(({ tx }) => tx)
}

function groupAppointmentsIntoLinkedGroups(
  appointments: AppointmentWithContainerInfo[]
): AppointmentWithContainerInfo[][] {
  const appointmentsByKey = new Map<string, AppointmentWithContainerInfo>()
  const adjacency = new Map<string, Set<string>>()

  for (const apptInfo of appointments) {
    const key = makeAppointmentKey(apptInfo.appointment)
    appointmentsByKey.set(key, apptInfo)
    adjacency.set(key, new Set())

    for (const linked of apptInfo.appointment.linked_appointments) {
      const lk = makeAppointmentKey(linked)
      adjacency.get(key)!.add(lk)
      if (!adjacency.has(lk)) adjacency.set(lk, new Set())
      adjacency.get(lk)!.add(key)
    }
  }

  const visited = new Set<string>()
  const groups: AppointmentWithContainerInfo[][] = []

  for (const key of adjacency.keys()) {
    if (visited.has(key)) continue
    const stack = [key]
    const group: AppointmentWithContainerInfo[] = []

    while (stack.length) {
      const curr = stack.pop()!
      if (visited.has(curr)) continue
      visited.add(curr)
      const info = appointmentsByKey.get(curr)
      if (info) group.push(info)
      for (const neigh of adjacency.get(curr)!) {
        if (!visited.has(neigh)) stack.push(neigh)
      }
    }

    groups.push(group)
  }

  return groups
}

export function createTransactionFromGroup(
  group: AppointmentWithContainerInfo[]
): AppointmentTransaction {
  const tx = new AppointmentTransaction()
  const [inb, outb] = splitInboundOutbound(group)
  tx.inbound_appointment = selectBestInboundAppointment(inb)
  tx.outbound_appointment = selectBestOutboundAppointment(outb)

  const moveTypes = Array.from(
    new Set(group.map((ai) => ai.appointment.moveType))
  )
    .sort()
    .join('|')
  const ids = group.map((ai) => ai.appointment.id).sort((a, b) => a - b)
  tx.stable_transaction_key = `${group[0].appointment.container_number}-${moveTypes}-${ids.join(',')}`

  if (
    tx.inbound_appointment &&
    tx.outbound_appointment &&
    tx.inbound_appointment.appointment.sortable_shift_key !==
      tx.outbound_appointment.appointment.sortable_shift_key
  ) {
    console.error(
      'Inbound/outbound shift mismatch:',
      tx.inbound_appointment.appointment.sortable_shift_key,
      tx.outbound_appointment.appointment.sortable_shift_key
    )
  }
  return tx
}

function splitInboundOutbound(
  group: AppointmentWithContainerInfo[]
): [AppointmentWithContainerInfo[], AppointmentWithContainerInfo[]] {
  const inbound: AppointmentWithContainerInfo[] = []
  const outbound: AppointmentWithContainerInfo[] = []

  for (const ai of group) {
    if (ai.appointment.direction === TransactionDirection.Inbound) {
      inbound.push(ai)
    } else if (ai.appointment.direction === TransactionDirection.Outbound) {
      outbound.push(ai)
    }
  }

  return [inbound, outbound]
}

function compareAppointments(a: Appointment, b: Appointment): number {
  const diff = a.window_start.toMillis() - b.window_start.toMillis()
  return diff !== 0 ? diff : a.id - b.id
}

function selectBestAppointment(
  appts: AppointmentWithContainerInfo[]
): AppointmentWithContainerInfo {
  return appts.sort((x, y) =>
    compareAppointments(x.appointment, y.appointment)
  )[0]
}

function selectBestInboundAppointment(
  appts: AppointmentWithContainerInfo[]
): AppointmentWithContainerInfo | undefined {
  return appts.length ? selectBestAppointment(appts) : undefined
}

function selectBestOutboundAppointment(
  appts: AppointmentWithContainerInfo[]
): AppointmentWithContainerInfo | undefined {
  return appts.length ? selectBestAppointment(appts) : undefined
}

function makeAppointmentKey(
  appointment: Appointment | LinkedAppointment
): string {
  return `${appointment.container_number}!!${appointment.terminal_reference}`
}

function limitToReleventTransactionsPerContainerAndMoveType(
  appointments: AppointmentWithContainerInfo[]
): AppointmentWithContainerInfo[] {
  const map = new Map<string, AppointmentWithContainerInfo[]>()
  for (const ai of appointments) {
    const key = `${ai.appointment.container_number}-${ai.appointment.moveType}`
    const arr = map.get(key) ?? []
    arr.push(ai)
    map.set(key, arr)
  }

  const results: AppointmentWithContainerInfo[] = []
  for (const arr of map.values()) {
    results.push(selectBestAppointment(arr))
  }
  return results
}
